import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Banner, Button, Card, Details, Dialog, List, ListItem, Select, TextField, useSnackbar,
} from '@meshble/molo';
import { api, must, mustOk } from '../lib/api';
import { localeIntl, t } from '../lib/lingua';
import { quandoRelativo } from '../lib/attivita';
import { usePermessi, type Permessi } from '../session';
import { Copy, Key } from '../icons';
import { Anello } from '../brand';

/**
 * I token API dell'organizzazione.
 *
 * Un token agisce **come** chi lo crea, ristretto dal profilo: non è
 * un'identità in più, è una procura su una che c'è già. Serve a far parlare
 * con Molo qualcosa che non è un browser — uno script, un assistente — e
 * quello che fa finisce nel registro con il suo nome.
 *
 * Il profilo e non la lista dei permessi: chi crea un token sceglie cosa gli
 * lascia fare, non trenta voci. Gli scope a mano restano all'API, per gli
 * script che li vogliono stretti.
 */

export type Profilo = 'lettura' | 'amministrazione';

/** I nomi dei profili, in italiano: si traducono con `t()` dove si mostrano. */
export const PROFILO: Record<Profilo, string> = {
  lettura: 'Lettura',
  amministrazione: 'Amministrazione',
};

/** Chi crea token: `token.create`, com'è lato hub. */
export function puoCreare(ha: Permessi): boolean {
  return ha('token.create');
}

/**
 * Il profilo «Amministrazione» vuol dire azioni, e le azioni via assistente
 * sono di owner e admin: chi ha `instance.update`, che il member non ha, è la
 * stessa soglia di `consentitoViaMCP`. All'admin l'owner concede i tool uno
 * per uno dalla pagina «Assistenti».
 */
export function puoAmministrare(ha: Permessi): boolean {
  return ha('instance.update');
}

export function TokenAPI({ slug }: { slug: string }) {
  const ha = usePermessi(slug);
  if (!puoCreare(ha)) return null;
  return <Elenco slug={slug} amministra={puoAmministrare(ha)} />;
}

/** I token dell'organizzazione: li leggono questa sezione e la pagina «Assistenti». */
export function useToken(slug: string) {
  return useQuery({
    queryKey: ['org-tokens', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/tokens', { params: { path: { slug } } })),
  });
}

/**
 * I token che valgono adesso, con la revoca. I revocati restano in tabella per
 * il registro; qui si guarda quello che vale. `soloProfili` tiene quelli nati da
 * un profilo, che sono i collegamenti degli assistenti.
 */
export function ElencoToken({ slug, soloProfili = false, vuoto }: { slug: string; soloProfili?: boolean; vuoto: string }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const q = useToken(slug);
  const vivi = (q.data ?? []).filter((tk) =>
    !tk.revokedAt && new Date(tk.expiresAt) > new Date() && (!soloProfili || !!tk.profile));

  const revoca = useMutation({
    mutationFn: (id: string) =>
      mustOk(api.DELETE('/api/v1/orgs/{slug}/tokens/{id}', { params: { path: { slug, id } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org-tokens', slug] });
      avvisa(t('Token revocato: smette di valere adesso'));
    },
  });

  if (q.error) return <Banner tone="error" title={t('Non riesco a leggere i token')}>{q.error.message}</Banner>;
  if (vivi.length === 0) {
    return (
      <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>{vuoto}</p>
    );
  }
  return (
    <List>
      {vivi.map((tk) => (
        <ListItem
          key={tk.id}
          headline={tk.label}
          supporting={[
            tk.profile ? t(PROFILO[tk.profile as Profilo] ?? tk.profile) : t('Scope a mano'),
            tk.email,
            t('scade {data}', { data: new Date(tk.expiresAt).toLocaleDateString(localeIntl()) }),
            tk.lastUsedAt ? t('usato {quando}', { quando: quandoRelativo(tk.lastUsedAt) }) : t('mai usato'),
          ].join(' · ')}
          trailing={
            <Button variant="text" disabled={revoca.isPending} onClick={() => revoca.mutate(tk.id)}>
              {t('Revoca')}
            </Button>
          }
        />
      ))}
    </List>
  );
}

function Elenco({ slug, amministra }: { slug: string; amministra: boolean }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();

  const [apri, setApri] = useState(false);
  const [label, setLabel] = useState('');
  const [profilo, setProfilo] = useState<Profilo>('lettura');
  const [giorni, setGiorni] = useState('30');
  const [valore, setValore] = useState<{ label: string; value: string } | null>(null);

  const invalida = () => qc.invalidateQueries({ queryKey: ['org-tokens', slug] });

  const crea = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/tokens', {
        params: { path: { slug } },
        body: { label: label.trim(), profile: profilo, days: Number(giorni) },
      })),
    onSuccess: (t) => {
      invalida();
      setApri(false);
      setLabel('');
      setValore({ label: t.label, value: t.value });
    },
  });

  const copia = () => {
    if (!valore) return;
    void navigator.clipboard.writeText(valore.value);
    avvisa(t('Token copiato'));
  };

  const pronto = label.trim() !== '' && !crea.isPending;

  return (
    <Card variant="outlined">
      <div className="md-section-head">
        <h2 className="md-title-medium">{t('Token API')}</h2>
        <Button variant="tonal" icon={<Key />} onClick={() => setApri(true)}>{t('Crea un token')}</Button>
      </div>

      {valore && (
        <Banner
          tone="success"
          title={t('Il token «{nome}» è pronto', { nome: valore.label })}
          action={<Button variant="text" onClick={() => setValore(null)}>{t('Fatto')}</Button>}
        >
          <div className="md-stack" style={{ marginTop: 'var(--md-sys-spacing-2)' }}>
            <span>
              {t('Copialo adesso: {nonLoRivedrai}. Molo ne tiene solo l’impronta, quindi nemmeno noi possiamo mostrartelo di nuovo.', {
                nonLoRivedrai: <strong>{t('non lo rivedrai')}</strong>,
              })}
            </span>
            <div className="md-copia">
              <TextField label="" aria-label={t('Il token')} readOnly value={valore.value}
                onFocus={(e) => e.currentTarget.select()} />
              <Button variant="outlined" icon={<Copy />} onClick={copia}>{t('Copia')}</Button>
            </div>
          </div>
        </Banner>
      )}

      <ElencoToken
        slug={slug}
        vuoto={t('Nessun token attivo. Un token serve a far lavorare su Molo qualcosa che non è il browser: uno script, o un assistente.')}
      />

      <Details summary={t('Cosa può fare un token')}>
        <p className="md-body-small" style={{ margin: 0 }}>
          {t('Quello che puoi fare tu, ristretto dal profilo: un token non dà a nessuno più diritti di quelli di chi lo crea, e se la persona viene tolta dall’organizzazione o disattivata i suoi token smettono di valere. Ogni cosa fatta con un token finisce nel registro con il tuo nome e con quello del token.')}
        </p>
        <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
          {t('{lettura} guarda server, istanze, log, metriche e backup.', {
            lettura: <strong>{t(PROFILO.lettura)}</strong>,
          })}{' '}
          {t('{amministrazione} aggiunge le azioni che un assistente può fare — riavvii, backup, copie di prova, aggiornamenti — ed è solo per amministratori e proprietari; un amministratore usa solo quelle che il proprietario gli ha concesso.', {
            amministrazione: <strong>{t(PROFILO.amministrazione)}</strong>,
          })}{' '}
          {t('Segreti, accessi, persone e cancellazioni restano nel pannello, con qualsiasi token.')}
        </p>
      </Details>

      <Dialog
        open={apri}
        onOpenChange={(v) => !v && setApri(false)}
        title={t('Crea un token')}
        description={t('Il valore si vede una volta sola, appena creato: tienilo pronto dove andrà usato.')}
        actions={
          <>
            <Button variant="text" onClick={() => setApri(false)}>{t('Annulla')}</Button>
            <Button
              variant="filled"
              icon={crea.isPending ? <Anello /> : undefined}
              disabled={!pronto}
              onClick={() => crea.mutate()}
            >
              {t('Crea')}
            </Button>
          </>
        }
      >
        <form className="md-form" onSubmit={(e) => { e.preventDefault(); if (pronto) crea.mutate(); }}>
          {crea.error && <Banner tone="error" title={t('Non l’ho creato')}>{(crea.error as Error).message}</Banner>}
          <TextField label={t('Nome')} value={label} autoFocus maxLength={60} onChange={(e) => setLabel(e.target.value)}
            placeholder={t('Claude Code sul mio portatile')} help={t('Serve a riconoscerlo qui e nel registro.')} />
          <Select
            label={t('Profilo')}
            value={profilo}
            options={[
              { value: 'lettura', label: t('Lettura — guarda, non cambia niente') },
              ...(amministra
                ? [{ value: 'amministrazione', label: t('Amministrazione — anche le azioni') }]
                : []),
            ]}
            onChange={(e) => setProfilo(e.target.value as Profilo)}
          />
          <Select
            label={t('Durata')}
            value={giorni}
            options={[
              { value: '30', label: t('{n} giorni', { n: 30 }) },
              { value: '90', label: t('{n} giorni', { n: 90 }) },
              { value: '365', label: t('Un anno') },
            ]}
            onChange={(e) => setGiorni(e.target.value)}
          />
        </form>
      </Dialog>
    </Card>
  );
}
